import { faSpinner } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useContext, useEffect, useRef, useState } from 'react'
import { CheckSquare, ClockHistory, Pencil, PeopleFill, PersonAdd, PersonLinesFill, PersonPlusFill, PersonX, PlusCircle, Square } from 'react-bootstrap-icons'
import useDebounce from '../../../components/Hooks/useDebounce'
import DateTimePickerFunctions from '../../../components/ui/calendar/date_picket/DateTimePickerComponents/DateTimePickerFunctions'
import Access from '../../../libs/api/services/Access'
import Authentication from '../../../libs/api/services/Authentication'
import Patient from '../../../libs/api/services/Patient'
import Team from '../../../libs/api/services/Team'
import User from '../../../libs/api/services/User'
import CommonFunctionsContext from '../../common/CommonFunctionsProvider'

export default function FacilityTeams() {
    const [team, setTeam] = useState({
        teamName: undefined,
        specialty: undefined,
        members: [],
        facility: { id: Authentication.getFacility() },
    })
    const [mode, setMode] = useState()
    return (
        <div className='d-flex flex-column h-100 m-3 flex-grow-1' style={{ overflow: 'auto' }}>
            <h1 className='text-center'>Facility Team Management</h1>
            {
                {
                    'Add Team': <NewTeam team={team} setTeam={setTeam} onCancel={() => setMode()} />,
                }[mode] || <TeamsBreakDown
                    onAdd={() => {
                        setTeam({
                            teamName: undefined,
                            speciality: undefined,
                            members: [],
                            facility: { id: Authentication.getFacility() },
                        })
                        setMode('Add Team')
                    }}
                    onSelect={(team => {
                        setTeam(team)
                        setMode('Add Team')
                    })}
                />
            }
        </div>
    )
}

const TeamsBreakDown = ({ onAdd, onSelect }) => {
    const { showError } = useContext(CommonFunctionsContext)
    const [teams, setTeams] = useState([])

    useEffect(() => {
        const load = async () =>
            setTeams(await Team.GetFacilitiesTeams().catch(showError))
        load()
    }, [])
    return (<div>
        <div className='d-flex align-items-center justify-content-end'>
            <div className='flex-grow-1'>
                <input className='form-control' placeholder='Search Teams' />
            </div>
            <div className='ms-5'>
                <button onClick={onAdd} className='btn bg-secondary-container'><PlusCircle /> Add Team</button>
            </div>
        </div>

        <div className='w-100' style={{ cursor: 'pointer' }}>
            {teams && teams.map(team => <div key={team.id} onClick={() => onSelect(team)} className='d-flex align-items-center'><div className='flex-grow-1'><PeopleFill />{team.teamName}</div> <button className='btn'><Pencil /></button></div>)}

        </div>
    </div>)
}

const NewTeam = ({ onAdd, onCancel, team, setTeam }) => {
    const [showFull, setShowFull] = useState(false)
    const { createModal, showError } = useContext(CommonFunctionsContext)
    const [showAllUsers, setShowAllUsers] = useState(false)

    const handleSave = async (t) => {
        if (!t.teamName) return
        const res = await Team.UpsertFacilityTeam({
            ...t,
            members: []
        }).catch(showError)
        if (res)
            setTeam({ ...t, id: res.id })
    }

    const handleSetValue = async (newTeam) =>
        setTeam({ ...newTeam })

    const _handleAssignRole = async (user, role) => {
        try {
            if (!user.id) {
                user = await User.add(user).catch(showError)
            }

            let teamId = team.id
            if (!teamId) {
                const res = await Team.UpsertFacilityTeam({ ...team, members: [] }).catch(showError)
                teamId = res && res.id
                if (teamId) setTeam({ ...team, id: teamId })
            }

            const resAssign = await Team.AssignMember(teamId, user.id, role.role).catch(showError)
            if (resAssign) {
                const newAssignment = { ...team, id: teamId, members: [resAssign, ...(team.members || [])] }
                setTeam({ ...newAssignment })
            }
        } catch (e) {
            showError && showError(e)
        }
    }

    const handleToggleUsersTab = async () => {
        const isSuper = Authentication.isClaimValid('Super')
        if (!isSuper) {
            return showError && showError('Access denied: Super permission required')
        }

        try {
            if (!team.id) {
                const draftName = team.teamName && team.teamName.trim().length > 0 ? team.teamName : `New Team ${new Date().toISOString()}`
                const payload = { ...team, teamName: draftName, members: [] }
                const res = await Team.UpsertFacilityTeam(payload).catch(e => { throw e })
                if (res && res.id) setTeam({ ...team, id: res.id, teamName: draftName })
            }
            setShowAllUsers(!showAllUsers)
        } catch (e) {
            showError && showError(e)
        }
    }

    const _handleAddPerson = (type) => {
        createModal(
            <NewUserDetails roles={type !== 'Leading Clinician'} onAdd={res => _handleAssignRole(res.user, res.role ? res.role : { role: 'Leading Clinician' })} />,
            `Assign Member`,
            <PersonAdd />
        )
    }

    const handleRevokeMemeber = async (assistant) => {
        try {
            const res = await Team.RevokeMember(team.id, assistant.id).catch(showError)
            if (res) {
                const _members = [...(team.members || [])]
                const el = _members.find(a => a.id === assistant.id)
                if (el) el.currentStatus = 'No longer on team'
                const newAssignment = { ...team, members: _members }
                setTeam({ ...newAssignment })
            }
        } catch (e) {
            showError && showError(e)
        }
    }

    return (
        <>
            <div className='flex-shrink-1'>
                <div>
                    <label>Team Name</label>
                    <input onBlur={e => handleSetValue({ ...team, teamName: e.target.value })} defaultValue={team.teamName} className='form-control' placeholder='Team Name' />
                </div>
                <div>
                    <label>Specialty</label>
                    <input onBlur={e => handleSetValue({ ...team, speciality: e.target.value })} defaultValue={team.speciality} className='form-control' placeholder='Specialty' />
                </div>

                {Authentication.isClaimValid('System Admin') && (
                    <div className='d-flex justify-content-end mt-2'>
                        <button
                            onClick={handleToggleUsersTab}
                            className={`btn ${showAllUsers ? 'bg-primary-container' : 'bg-secondary-container'}`}
                            title={'System Admin only; view and assign all patients from across all facilities'}
                        >
                            All System Patients
                        </button>
                    </div>
                )}

                {team.teamName && (
                    <>
                        <div className='d-flex'>
                            <div className='flex-grow-1'><b>Members</b></div>
                            <div className=''><button onClick={() => setShowFull(!showFull)} className={`btn ${showFull && 'bg-primary-container'}`}><ClockHistory />{`${!showFull ? 'Show' : 'Hide'}`} History</button></div>
                        </div>
                        <button onClick={() => _handleAddPerson('Assistant')} className='btn w-100'>
                            <PersonPlusFill />Add Member
                        </button>
                        <div className='row'>
                            <div className='col-2 text-center '><b>Who</b></div>
                            <div className='col-2 text-center '><b>Role</b></div>
                            <div className='col-1 text-center '><b>Current Status</b></div>
                            <div className='col-2 text-center '><b>Since</b></div>
                            <div className='col-2 text-center '><b>Last Update</b></div>
                            <div className='col-3 text-center '><b>Options</b></div>
                        </div>
                    </>
                )}
            </div>

            {/* Render tab content area */}
            {showAllUsers ? (
                <AllUsersTab team={team} setTeam={setTeam} showError={showError} />
            ) : (
                team.teamName && (
                    <div className='d-flex flex-column m-2 flex-grow-1' style={{ overflow: 'auto' }}>
                        {team.members && team.members.filter(a => (!showFull ? a.currentStatus === 'Assigned' : true)).map((assistant, idx) => (
                            <div className='row' key={assistant.id || idx}>
                                <div className='col-2'><PersonLinesFill /> {`${assistant.user.firstName} ${assistant.user.lastName}`}</div>
                                <div className='col-2 text-center'>{assistant.role.role}</div>
                                <div className='col-1 text-center'>{assistant.currentStatus}</div>
                                <div className='col-2'>{DateTimePickerFunctions.LocalISO(new Date(assistant.created.substring(0, 19)), 'h')}</div>
                                <div className='col-2'>{DateTimePickerFunctions.LocalISO(new Date(assistant.lastUpdate.substring(0, 19)), 'h')}</div>
                                <div className='col-3 text-end'>
                                    {assistant.currentStatus === 'Assigned' && (
                                        <button onClick={() => handleRevokeMemeber(assistant)} className='btn text-end m-0 p-0'>
                                            {`No longer a ${assistant.role.role} in ${team.teamName}?`} <PersonX />
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )
            )}

            <div className='text-center'>
                <button onClick={() => handleSave(team).then(_ => onCancel())} className='btn bg-primary-container'>Close</button>
            </div>
        </>
    )
}

const AllUsersTab = ({ team, setTeam, showError }) => {
    const [patients, setPatients] = useState([])
    const [loading, setLoading] = useState(false)
    const [assignedPatientIds, setAssignedPatientIds] = useState([])
    const [loadingAssignments, setLoadingAssignments] = useState(false)

    const loadPatients = async () => {
        try {
            setLoading(true)
            console.log('Loading ALL patients from entire system (all facilities)')

            // Use the new system-wide endpoint that returns all patients
            const allPatients = await Patient.ListAllPatientsSystemWide()
            console.log('Loaded patients:', allPatients)
            console.log('Patient count:', allPatients?.length || 0)
            if (allPatients && allPatients.length > 0) {
                console.log('Sample patient structure:', allPatients[0])
            }

            setPatients(allPatients || [])
        } catch (e) {
            console.error('Error loading patients:', e)
            showError && showError(`Error loading patients: ${e.message || e}`)
        } finally {
            setLoading(false)
        }
    }

    const loadPatientAssignments = async () => {
        if (!team.id) {
            console.log('No team ID available')
            return
        }

        try {
            setLoadingAssignments(true)
            // Load only the patient IDs that are assigned to this team (much more efficient)
            console.log('Loading assignments for team:', team.id)
            console.log('Team object:', team)
            const patientIds = await Team.GetTeamPatientIds(team.id)
            console.log('Assigned patient IDs:', patientIds)
            console.log('Number of assigned patients:', patientIds?.length || 0)
            setAssignedPatientIds(patientIds || [])
        } catch (e) {
            console.error('Error loading patient assignments:', e)
            showError && showError(`Failed to load patient assignments: ${e.message || e}`)
        } finally {
            setLoadingAssignments(false)
        }
    }

    useEffect(() => {
        loadPatients()
    }, [])

    useEffect(() => {
        if (team.id) {
            loadPatientAssignments()
        }
    }, [team.id])

    const isPatientAssigned = (patientId) => {
        return assignedPatientIds.includes(patientId)
    }

    const togglePatientAssignment = async (patient) => {
        try {
            if (!team.id) return showError && showError('Please save the team first')

            const isAssigned = isPatientAssigned(patient.id)

            if (isAssigned) {
                // Revoke patient from team
                await Team.RevokePatient(team.id, patient.id).catch(e => { throw e })
                // Update local state - remove from assignments
                setAssignedPatientIds(prev => prev.filter(id => id !== patient.id))
            } else {
                // Assign patient to team
                const res = await Team.AssignPatient(team.id, patient.id).catch(e => { throw e })
                if (res) {
                    setAssignedPatientIds(prev => [...prev, patient.id])
                }
            }
        } catch (e) {
            showError && showError(e)
        }
    }

    return (
        <div className='d-flex flex-column m-2 flex-grow-1' style={{ overflow: 'auto' }}>
            <h5>All System Patients {patients.length > 0 && `(${patients.length})`}</h5>
            {(loading || loadingAssignments) && (
                <div className='text-primary mb-2'>
                    <span className='spinner-border spinner-border-sm me-2' role='status' aria-hidden='true'></span>
                    {loading ? 'Loading patients from all facilities...' : 'Loading team assignments...'}
                </div>
            )}
            <p className='text-muted small'>Select patients to assign to this team. Showing all patients across all facilities in the system.</p>
            {!loading && patients.length === 0 && (
                <div className='alert alert-info'>
                    No patients found in the system. Patients need to be registered before they can be assigned to teams.
                </div>
            )}
            <div>
                {patients && patients
                    .filter(p => {
                        // Filter out patients with no name data
                        const firstName = p.userProfile?.firstName || p.firstName || ''
                        const lastName = p.userProfile?.lastName || p.lastName || ''
                        return firstName.trim().length > 0 || lastName.trim().length > 0
                    })
                    .map(p => {
                        const firstName = p.userProfile?.firstName || p.firstName || ''
                        const lastName = p.userProfile?.lastName || p.lastName || ''
                        const idNumber = p.userProfile?.idNumber || p.idNumber || ''
                        const email = p.userProfile?.emailAddress || p.emailAddress || ''

                        return (
                            <div className='d-flex align-items-center mb-2 p-2 border-bottom' key={p.id}>
                                <input
                                    type='checkbox'
                                    checked={isPatientAssigned(p.id)}
                                    onChange={() => togglePatientAssignment(p)}
                                    className='me-2'
                                    disabled={loadingAssignments}
                                    style={{ cursor: loadingAssignments ? 'not-allowed' : 'pointer' }}
                                />
                                <div className='flex-grow-1'>
                                    <div>
                                        <strong>{firstName} {lastName}</strong>
                                        {idNumber && <span className='text-muted ms-2'>({idNumber})</span>}
                                    </div>
                                    {email && <small className='text-muted'>{email}</small>}
                                </div>
                            </div>
                        )
                    })}
            </div>
        </div>
    )
}

const NewUserDetails = ({ close, onAdd, roles }) => {
    const { showError } = useContext(CommonFunctionsContext)
    const [facilityUsers, setFacilityUsers] = useState()
    const [apiRoles, setAPIRoles] = useState()
    const [role, setRole] = useState()
    const [selection, setSelection] = useState()
    const [profile, setProfile] = useState({
        idNumber: '',
        firstName: '',
        lastName: '',
        mobileNumber: '',
        emailAddress: '',
    })
    const [status, setStatus] = useState()
    const debouncedInputValue = useDebounce(profile.idNumber, 300);
    const IDNumberRef = useRef()

    useEffect(() => {
        if (debouncedInputValue && debouncedInputValue.length > 12) {
            setProfile({ id: undefined, firstName: '', lastName: '', mobileNumber: '', emailAddress: '' })
            setStatus('Searching for user profile')
            User.LookupByIDNumber(debouncedInputValue)
                .then(res => {
                    setTimeout(() => { setStatus() }, 1000)
                    if (res) setProfile({ ...res })
                }).catch(e => { setTimeout(() => { setStatus(); }, 1000) })
        } else setStatus()
    }, [debouncedInputValue])

    const _handleSubmit = (e) => {
        e.preventDefault()
        if ((profile.idNumber < 12)) return false
        onAdd({ user: { ...profile, idNumber: IDNumberRef.current && IDNumberRef.current.value ? IDNumberRef.current.value : profile.idNumber }, role: role })
            .then(() => close(() => { })).catch(() => { })
    }

    useEffect(() => {
        const load = async () => {
            setAPIRoles(await Access.ListFacilityRoles('Clinical').catch(showError))
            setFacilityUsers(await User.ListFacilitiesUsers('Clinical'))
        }
        if (roles) load()
    }, [roles])

    useEffect(() => { if (IDNumberRef.current) IDNumberRef.current.focus() }, [IDNumberRef])

    const handleSetFacilityUser = (user) => { setProfile(user) }

    return (
        <form onSubmit={_handleSubmit} className='d-flex flex-column flex-grow-1' style={{ maxHeight: '100%' }}>
            <div className='flex-grow-1 m-3' style={{ overflow: 'auto' }}>
                <h2>Basic Information</h2>
                <div>
                    <div className='d-flex'>
                        <button type='button' onClick={() => setSelection()} className={`btn w-50 ${selection !== 'Facility' ? 'bg-primary-container' : ''}`}>ID Number</button>
                        <button type='button' onClick={() => setSelection('Facility')} className={`btn w-50 ${selection === 'Facility' ? 'bg-primary-container' : ''}`}>Facility</button>
                    </div>
                    {selection ? (
                        <div className='d-flex flex-column'>
                            {facilityUsers && facilityUsers.map(user => <button className='btn text-start' type='button' onClick={() => handleSetFacilityUser(user)} key={user.id}>{user.firstName} {user.lastName}</button>)}
                        </div>
                    ) : (
                        <div>
                            <label>ID Number</label>
                            <input ref={IDNumberRef} value={profile.idNumber} onChange={e => setProfile({ ...profile, idNumber: e.target.value })} className='form-control' placeholder='ID Number' autoFocus />
                        </div>
                    )}
                </div>
                <div className='text-center'>
                    {status ? <><FontAwesomeIcon icon={faSpinner} spin /> {status}</> : <div className='p-2' />}
                </div>
                {((IDNumberRef.current && IDNumberRef.current.value && IDNumberRef.current.value.length >= 13) || (profile && profile.id !== undefined)) && (
                    <>
                        <div className='d-flex'>
                            <div className='w-50 pe-2'>
                                <label>First Name</label>
                                <input required value={profile.firstName} onChange={e => setProfile({ ...profile, firstName: e.target.value })} className='form-control' placeholder='First Name' />
                            </div>
                            <div className='w-50 ps-2'>
                                <label>Last Names</label>
                                <input required value={profile.lastName} onChange={e => setProfile({ ...profile, lastName: e.target.value })} className='form-control' placeholder='Last Names' />
                            </div>
                        </div>
                        <div className='d-flex'>
                            <div className='w-50 pe-2'>
                                <label>Mobile</label>
                                <input required value={profile.mobileNumber} onChange={e => setProfile({ ...profile, mobileNumber: e.target.value })} className='form-control' placeholder='Mobile' />
                            </div>
                            <div className='w-50 ps-2'>
                                <label>Email</label>
                                <input required value={profile.emailAddress} onChange={e => setProfile({ ...profile, emailAddress: e.target.value })} className='form-control' placeholder='Email' />
                            </div>
                        </div>

                        {roles && (
                            <div>
                                <h2>Assign Role</h2>
                                <div className='d-flex flex-column align-items-start'>
                                    {apiRoles && apiRoles.map(aRole =>
                                        <button type='button' className='btn' onClick={_ => setRole(aRole)} id={aRole.id} key={aRole.id}>
                                            {role && role.id === aRole.id ? <CheckSquare /> : <Square />} {aRole.role}
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            <div className='text-center flex-shrink-1'><button type='submit' className='btn bg-primary-container'>Assign</button></div>
        </form>
    )
}